import React, { useEffect, useRef, useState } from "react";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import apiService from "../services/ApiService";

interface ScanResultModalProps {
  isOpen: boolean;
  result: "pass" | "fail" | "lock" | null;
  codes: string[];
  onConfirm: () => void;
  onManagerApproval?: (password: string) => Promise<boolean>;
}

export const ScanResultModal: React.FC<ScanResultModalProps> = ({
  isOpen,
  result,
  codes,
  onConfirm,
}) => {
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  // NEW: Add current time state for live updates
  const [currentTime, setCurrentTime] = useState(new Date());
  const currentShiftId = localStorage.getItem("current_shift_id");

  // NEW: Update current time every second for accurate time display
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timeInterval);
    };
  }, []);

  // Poll shift detail every 10 seconds when modal is open and result is "fail" or "lock"
  useEffect(() => {
    if (isOpen && (result === "fail" || result === "lock") && currentShiftId) {
      const pollShiftStatus = async () => {
        try {
          setLastChecked(new Date());
          // Use cache bypass for real-time data
          const shiftDetail = await apiService.getShiftDetail(currentShiftId, {
            bypassCache: true,
          });

          if (shiftDetail?.status === "active") {
            console.log("Shift status changed to active, closing modal");
            // Auto-close modal when shift becomes active
            onConfirm();
          }
        } catch (error) {
          console.error("Failed to poll shift status:", error);
        }
      };

      // Poll immediately and then every 10 seconds
      pollShiftStatus();
      pollingInterval.current = setInterval(pollShiftStatus, 10000);
    }

    // Cleanup polling when modal closes or result changes
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [isOpen, result, currentShiftId, onConfirm]);

  // Format time since last check (now uses currentTime for live updates)
  const formatTimeSinceLastCheck = () => {
    if (!lastChecked) return "";
    const now = currentTime; // Use state instead of new Date()
    const diffSeconds = Math.floor(
      (now.getTime() - lastChecked.getTime()) / 1000
    );

    if (diffSeconds < 5) {
      return "Just now";
    } else if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    } else {
      return `${Math.floor(diffSeconds / 60)}m ago`;
    }
  };

  if (!result) return null;

  // For "pass" result, show just a confirm button overlay instead of full modal
  if (result === "pass") {
    return (
      <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">PASS</h3>
              <p className="text-gray-600">All barcodes match successfully!</p>
            </div>
            <Button
              variant="primary"
              onClick={onConfirm}
              className="px-8 py-3 text-lg"
              size="lg"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // For "fail" and "lock" results, show full modal
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Disable close functionality
      title={result === "lock" ? "Alert" : "Scan Result"}
      size="medium"
      showCloseButton={false} // Hide the close button
      footer={null} // No footer for fail/lock modals
    >
      <div className="text-center py-6">
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-red-600 mb-2">
              {result === "fail" ? "FAIL" : "LOCKED"}
            </h3>
            <p className="text-gray-600">
              {result === "fail"
                ? "Barcodes do not match! Please check the items."
                : "Scanning is locked. Please contact the manager."}
            </p>
            {result === "fail" && (
              <div className="mt-4 text-sm text-gray-500 space-y-1">
                <p>Scanned codes:</p>
                <div className="font-mono bg-gray-100 p-2 rounded space-y-1">
                  <p>Camera 1: {codes[0]}</p>
                  <p>Camera 2: {codes[1]}</p>
                  <p>Camera 3: {codes[2]}</p>
                </div>
              </div>
            )}

            {/* Enhanced manager approval section with polling indicator */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm text-orange-600 font-medium mb-3">
                Please ask Manager's approval to continue scanning
              </p>

              {/* Enhanced polling indicator with last check time */}
              {(result === "fail" || result === "lock") && (
                <div className="flex flex-col items-center gap-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                    <span>Checking for manager approval...</span>
                  </div>
                  {lastChecked && (
                    <div className="text-gray-400">
                      Last checked: {formatTimeSinceLastCheck()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
